import { useState, useCallback } from 'react';
import { trainingManagementService, TrainingCourse } from '../services/trainingManagementService';

export const useTrainingManagement = () => {
  const [courses, setCourses] = useState<TrainingCourse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCourses = useCallback(async (params?: any) => {
    setLoading(true);
    setError(null);
    try {
      const data = await trainingManagementService.getCourses(params);
      setCourses(data.courses);
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch courses');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createCourse = useCallback(async (data: Partial<TrainingCourse>) => {
    setLoading(true);
    setError(null);
    try {
      const course = await trainingManagementService.createCourse(data);
      // Refresh courses list
      await fetchCourses();
      return course;
    } catch (err: any) {
      setError(err.message || 'Failed to create course');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchCourses]);

  const updateCourse = useCallback(async (courseId: string, data: Partial<TrainingCourse>) => {
    setLoading(true);
    setError(null);
    try {
      const course = await trainingManagementService.updateCourse(courseId, data);
      // Update local state
      setCourses(prev => 
        prev.map(c => c.id === courseId ? course : c)
      );
      return course;
    } catch (err: any) {
      setError(err.message || 'Failed to update course');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const publishCourse = useCallback(async (courseId: string) => {
    setLoading(true);
    setError(null);
    try {
      const course = await trainingManagementService.publishCourse(courseId);
      // Update local state
      setCourses(prev => 
        prev.map(c => c.id === courseId ? course : c)
      );
      return course;
    } catch (err: any) {
      setError(err.message || 'Failed to publish course');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const archiveCourse = useCallback(async (courseId: string) => {
    setLoading(true);
    setError(null);
    try {
      const course = await trainingManagementService.archiveCourse(courseId);
      // Update local state
      setCourses(prev => 
        prev.map(c => c.id === courseId ? course : c)
      );
      return course;
    } catch (err: any) {
      setError(err.message || 'Failed to archive course');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const duplicateCourse = useCallback(async (courseId: string, newTitle?: string) => {
    setLoading(true);
    setError(null);
    try {
      const course = await trainingManagementService.duplicateCourse(courseId, newTitle);
      // Refresh courses list
      await fetchCourses();
      return course;
    } catch (err: any) {
      setError(err.message || 'Failed to duplicate course');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchCourses]);

  const deleteCourse = useCallback(async (courseId: string) => {
    setLoading(true);
    setError(null);
    try {
      await trainingManagementService.deleteCourse(courseId);
      // Remove from local state
      setCourses(prev => prev.filter(c => c.id !== courseId));
    } catch (err: any) {
      setError(err.message || 'Failed to delete course');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    courses,
    loading,
    error,
    fetchCourses,
    createCourse,
    updateCourse,
    publishCourse,
    archiveCourse,
    duplicateCourse,
    deleteCourse,
  };
};

